import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/ToastContainer';
import * as api from '../services/api';
import '../styles/admin-ui.css';
import './AdminDownloads.css';

const EMPTY_FORM = {
  platform: '',
  platform_name: '',
  platform_icon: '',
  version: '',
  file_size: '',
  download_url: '',
  download_type: 'link',
  min_requirements: '',
  system_requirements: '{\n  "cpu": "",\n  "ram": "",\n  "disk": "",\n  "network": "",\n  "os": ""\n}',
  description: '',
  changelog: '',
  architectures: '',
  checksum_sha256: '',
  display_order: 0,
};

const formatSysReq = (value) => {
  if (!value) return EMPTY_FORM.system_requirements;
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return EMPTY_FORM.system_requirements;
  }
};

const parseSysReqPreview = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const AdminDownloads = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingDownload, setEditingDownload] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    loadDownloads();
  }, []);

  const stats = useMemo(() => {
    const visible = downloads.filter((d) => d.is_visible).length;
    const totalCount = downloads.reduce((sum, d) => sum + (d.download_count || 0), 0);
    return {
      total: downloads.length,
      visible,
      hidden: downloads.length - visible,
      downloads: totalCount,
    };
  }, [downloads]);

  const loadDownloads = async () => {
    setLoading(true);
    try {
      const data = await api.getAdminDownloads();
      setDownloads(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load downloads:', error);
      showError(error.message || t('admin.downloads.loadError', 'Failed to load downloads'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = async (downloadId) => {
    try {
      await api.toggleDownloadVisibility(downloadId);
      showSuccess(t('admin.downloads.visibilityUpdated', 'Visibility updated'));
      loadDownloads();
    } catch (error) {
      console.error('Failed to toggle visibility:', error);
      showError(error.message || t('admin.downloads.visibilityError', 'Could not change visibility'));
    }
  };

  const handleDelete = async (downloadId) => {
    if (!window.confirm(t('admin.downloads.deleteConfirm', 'Delete this download platform?'))) {
      return;
    }
    try {
      await api.deleteDownload(downloadId);
      showSuccess(t('admin.downloads.deleted', 'Download deleted'));
      loadDownloads();
    } catch (error) {
      console.error('Failed to delete download:', error);
      showError(error.message || t('admin.downloads.deleteError', 'Delete failed'));
    }
  };

  const openEditModal = (download) => {
    setEditingDownload(download);
    setFormData({
      platform: download.platform || '',
      platform_name: download.platform_name || '',
      platform_icon: download.platform_icon || '',
      version: download.version || '',
      file_size: download.file_size || '',
      download_url: download.download_url || '',
      download_type: download.download_type || 'link',
      min_requirements: download.min_requirements || '',
      system_requirements: formatSysReq(download.system_requirements),
      description: download.description || '',
      changelog: download.changelog || '',
      architectures: download.architectures || '',
      checksum_sha256: download.checksum_sha256 || '',
      display_order: download.display_order ?? 0,
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingDownload(null);
    setFormData({
      ...EMPTY_FORM,
      version: 'v1.0.0',
      display_order: downloads.length + 1,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingDownload(null);
  };

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let systemRequirements = formData.system_requirements?.trim() || '';
    if (systemRequirements) {
      try {
        systemRequirements = JSON.stringify(JSON.parse(systemRequirements));
      } catch {
        showError(t('admin.downloads.invalidJson', 'System requirements must be valid JSON'));
        return;
      }
    }

    const submitData = {
      platform: formData.platform.trim(),
      platform_name: formData.platform_name.trim(),
      platform_icon: formData.platform_icon.trim(),
      version: formData.version.trim(),
      file_size: formData.file_size.trim(),
      download_url: formData.download_url.trim(),
      download_type: formData.download_type || 'link',
      min_requirements: formData.min_requirements.trim(),
      system_requirements: systemRequirements,
      description: formData.description.trim(),
      changelog: formData.changelog.trim(),
      architectures: formData.architectures.trim(),
      checksum_sha256: formData.checksum_sha256.trim(),
      display_order: parseInt(formData.display_order, 10) || 0,
    };

    setSaving(true);
    try {
      if (editingDownload) {
        await api.updateDownload(editingDownload.id, submitData);
        showSuccess(t('admin.downloads.updated', 'Download updated'));
      } else {
        await api.createDownload(submitData);
        showSuccess(t('admin.downloads.created', 'Download created'));
      }
      setShowModal(false);
      setEditingDownload(null);
      loadDownloads();
    } catch (error) {
      console.error('Failed to save download:', error);
      showError(error.message || t('admin.downloads.saveError', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="aui-page">
        <div className="aui-empty">
          <div className="aui-spinner" />
          <p>{t('common.loading', 'Loading…')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="aui-page">
      <button type="button" className="aui-back" onClick={() => navigate('/dashboard/admin')}>
        ← {t('common.back', 'Back to Admin')}
      </button>

      <div className="aui-hero">
        <div>
          <p className="aui-kicker">{t('admin.downloads.kicker', 'Client builds')}</p>
          <h1>{t('admin.downloads.title', 'Downloads')}</h1>
          <p className="aui-lead">
            {t(
              'admin.downloads.subtitle',
              'Publish platform builds, requirements, checksums, and release notes.'
            )}
          </p>
        </div>
        <div className="aui-hero-actions">
          <button type="button" className="aui-btn ghost" onClick={loadDownloads}>
            {t('common.refresh', 'Refresh')}
          </button>
          <button type="button" className="aui-btn primary" onClick={openCreateModal}>
            {t('admin.downloads.add', 'Add platform')}
          </button>
        </div>
      </div>

      <div className="aui-stats">
        <article className="aui-stat">
          <span>{t('admin.downloads.stats.total', 'Platforms')}</span>
          <strong>{stats.total}</strong>
        </article>
        <article className="aui-stat">
          <span>{t('admin.downloads.stats.visible', 'Visible')}</span>
          <strong>{stats.visible}</strong>
        </article>
        <article className="aui-stat">
          <span>{t('admin.downloads.stats.hidden', 'Hidden')}</span>
          <strong>{stats.hidden}</strong>
        </article>
        <article className="aui-stat">
          <span>{t('admin.downloads.stats.count', 'Total downloads')}</span>
          <strong>{stats.downloads}</strong>
        </article>
      </div>

      {downloads.length === 0 ? (
        <div className="aui-empty aui-panel">
          <h3>{t('admin.downloads.emptyTitle', 'No platforms yet')}</h3>
          <p>{t('admin.downloads.emptyDesc', 'Add a client build to publish on the downloads page.')}</p>
          <button type="button" className="aui-btn primary" onClick={openCreateModal}>
            {t('admin.downloads.add', 'Add platform')}
          </button>
        </div>
      ) : (
        <div className="aui-grid cards-2 aui-downloads-grid">
          {downloads.map((download) => {
            const sysReq = parseSysReqPreview(download.system_requirements);
            return (
              <article
                key={download.id}
                className={`aui-card aui-download-card ${download.is_visible ? '' : 'is-hidden'}`}
              >
                <div className="aui-download-top">
                  <div>
                    <p className="aui-download-platform">{download.platform}</p>
                    <h3>{download.platform_name}</h3>
                  </div>
                  <span className={`aui-chip ${download.is_visible ? 'ok' : 'warn'}`}>
                    {download.is_visible
                      ? t('admin.downloads.visible', 'Visible')
                      : t('admin.downloads.hidden', 'Hidden')}
                  </span>
                </div>

                <div className="aui-download-meta">
                  <div>
                    <span>{t('admin.downloads.version', 'Version')}</span>
                    <strong>{download.version || '—'}</strong>
                  </div>
                  <div>
                    <span>{t('admin.downloads.size', 'Size')}</span>
                    <strong>{download.file_size || '—'}</strong>
                  </div>
                  <div>
                    <span>{t('admin.downloads.type', 'Type')}</span>
                    <strong>{download.download_type === 'store' ? 'Store' : 'Link'}</strong>
                  </div>
                  <div>
                    <span>{t('admin.downloads.count', 'Downloads')}</span>
                    <strong>{download.download_count || 0}</strong>
                  </div>
                </div>

                {download.description && <p className="aui-download-desc">{download.description}</p>}

                <div className="aui-download-details">
                  <p>
                    <span>{t('admin.downloads.requirements', 'Requirements')}</span>
                    {download.min_requirements || '—'}
                  </p>
                  <p>
                    <span>{t('admin.downloads.architectures', 'Architectures')}</span>
                    {download.architectures || '—'}
                  </p>
                  <p>
                    <span>{t('admin.downloads.order', 'Order')}</span>
                    {download.display_order ?? 0}
                  </p>
                  {download.download_url && (
                    <p className="aui-download-url" title={download.download_url}>
                      <span>URL</span>
                      {download.download_url}
                    </p>
                  )}
                  {download.checksum_sha256 && (
                    <p className="aui-download-checksum" title={download.checksum_sha256}>
                      <span>SHA-256</span>
                      <code>{download.checksum_sha256}</code>
                    </p>
                  )}
                </div>

                {(sysReq.cpu || sysReq.ram || sysReq.disk || sysReq.os) && (
                  <details className="aui-download-sys">
                    <summary>{t('admin.downloads.systemReq', 'System requirements')}</summary>
                    <ul>
                      {sysReq.os && <li>OS: {sysReq.os}</li>}
                      {sysReq.cpu && <li>CPU: {sysReq.cpu}</li>}
                      {sysReq.ram && <li>RAM: {sysReq.ram}</li>}
                      {sysReq.disk && <li>Disk: {sysReq.disk}</li>}
                      {sysReq.network && <li>Network: {sysReq.network}</li>}
                    </ul>
                  </details>
                )}

                <div className="aui-row-actions">
                  <button type="button" onClick={() => openEditModal(download)}>
                    {t('common.edit', 'Edit')}
                  </button>
                  <button type="button" onClick={() => handleToggleVisibility(download.id)}>
                    {download.is_visible
                      ? t('admin.downloads.hide', 'Hide')
                      : t('admin.downloads.show', 'Show')}
                  </button>
                  <button type="button" className="danger" onClick={() => handleDelete(download.id)}>
                    {t('common.delete', 'Delete')}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="aui-modal-overlay" onClick={closeModal}>
          <div className="aui-modal wide" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h2>
              {editingDownload
                ? t('admin.downloads.editTitle', 'Edit platform')
                : t('admin.downloads.createTitle', 'Add platform')}
            </h2>
            <form className="aui-form" onSubmit={handleSubmit}>
              <div className="aui-form-grid">
                <label>
                  Platform ID
                  <input
                    type="text"
                    value={formData.platform}
                    onChange={(e) => updateField('platform', e.target.value)}
                    placeholder="windows, macos, linux…"
                    required
                    disabled={!!editingDownload}
                  />
                </label>
                <label>
                  Platform name
                  <input
                    type="text"
                    value={formData.platform_name}
                    onChange={(e) => updateField('platform_name', e.target.value)}
                    placeholder="Windows"
                    required
                  />
                </label>
                <label>
                  Icon label
                  <input
                    type="text"
                    value={formData.platform_icon}
                    onChange={(e) => updateField('platform_icon', e.target.value)}
                    placeholder="win / mac / linux"
                  />
                </label>
                <label>
                  Version
                  <input
                    type="text"
                    value={formData.version}
                    onChange={(e) => updateField('version', e.target.value)}
                    placeholder="v1.0.0"
                    required
                  />
                </label>
                <label>
                  File size
                  <input
                    type="text"
                    value={formData.file_size}
                    onChange={(e) => updateField('file_size', e.target.value)}
                    placeholder="45 MB"
                    required
                  />
                </label>
                <label>
                  Download type
                  <select
                    value={formData.download_type}
                    onChange={(e) => updateField('download_type', e.target.value)}
                  >
                    <option value="link">Direct link</option>
                    <option value="store">App store</option>
                  </select>
                </label>
              </div>

              <label>
                Download URL
                <input
                  type="url"
                  value={formData.download_url}
                  onChange={(e) => updateField('download_url', e.target.value)}
                  placeholder="https://"
                />
              </label>

              <div className="aui-form-grid">
                <label>
                  Minimum requirements
                  <input
                    type="text"
                    value={formData.min_requirements}
                    onChange={(e) => updateField('min_requirements', e.target.value)}
                    placeholder="Windows 10/11 (64-bit)"
                  />
                </label>
                <label>
                  Architectures
                  <input
                    type="text"
                    value={formData.architectures}
                    onChange={(e) => updateField('architectures', e.target.value)}
                    placeholder="x64, arm64"
                  />
                </label>
                <label>
                  Display order
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => updateField('display_order', e.target.value)}
                    min="0"
                  />
                </label>
              </div>

              <label>
                SHA-256 checksum
                <input
                  type="text"
                  value={formData.checksum_sha256}
                  onChange={(e) => updateField('checksum_sha256', e.target.value)}
                  placeholder="optional checksum"
                  spellCheck={false}
                />
              </label>

              <label>
                Description
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Short summary shown on the downloads page"
                />
              </label>

              <label>
                Changelog
                <textarea
                  rows={4}
                  value={formData.changelog}
                  onChange={(e) => updateField('changelog', e.target.value)}
                  placeholder="Release notes"
                />
              </label>

              <label>
                System requirements (JSON)
                <textarea
                  rows={7}
                  value={formData.system_requirements}
                  onChange={(e) => updateField('system_requirements', e.target.value)}
                  spellCheck={false}
                />
              </label>

              <div className="aui-form-actions">
                <button type="button" className="aui-btn ghost" onClick={closeModal} disabled={saving}>
                  {t('common.cancel', 'Cancel')}
                </button>
                <button type="submit" className="aui-btn primary" disabled={saving}>
                  {saving
                    ? t('common.saving', 'Saving…')
                    : editingDownload
                      ? t('common.save', 'Save changes')
                      : t('common.create', 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDownloads;
